import { useState, useEffect, useRef } from 'react';
import { postAPI } from '../services/api';
import PostCard from './PostCard';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Skeleton } from './ui/skeleton';

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerTarget = useRef(null);

  const fetchFeed = async (pageNum = 1) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await postAPI.getFeed(pageNum, 10);
      const newPosts = response.data.posts;

      if (pageNum === 1) {
        setPosts(newPosts);
      } else {
        setPosts((prev) => [...prev, ...newPosts]);
      }

      setHasMore(response.data.hasMore);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching feed:', error);
      if (pageNum === 1 && !error._isAuthError) {
        toast.error('Failed to load feed');
      }
      // Mark as no more posts to prevent infinite retry
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchFeed(1);
  }, []);

  // Infinite scroll implementation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchFeed(page + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, page]);

  const handleLike = async (postId) => {
    const post = posts.find((p) => p._id === postId);
    if (!post) return;
    
    const isLiked = post.liked;
    const currentUserId = localStorage.getItem('userId');

    // Optimistically update UI
    setPosts((prevPosts) =>
      prevPosts.map((p) => {
        if (p._id === postId) {
          const currentLikes = Array.isArray(p.likes) ? p.likes : [];
          return {
            ...p,
            liked: !isLiked,
            likes: isLiked 
              ? currentLikes.filter((id) => id !== currentUserId)
              : [...currentLikes, currentUserId],
          };
        }
        return p;
      })
    );

    try {
      const response = isLiked 
        ? await postAPI.unlikePost(postId)
        : await postAPI.likePost(postId);
      
      // Success - UI already updated
      return;
    } catch (error) {
      console.error('Error liking post:', error);
      
      // Revert optimistic update on error
      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          if (p._id === postId) {
            const currentLikes = Array.isArray(p.likes) ? p.likes : [];
            return {
              ...p,
              liked: isLiked, // Revert
              likes: isLiked 
                ? [...currentLikes, currentUserId]
                : currentLikes.filter((id) => id !== currentUserId),
            };
          }
          return p;
        })
      );
      
      toast.error('Failed to like post');
    }
  };

  const handleComment = async (postId, commentText) => {
    if (!commentText || !commentText.trim()) {
      return false;
    }

    try {
      const response = await postAPI.addComment(postId, commentText);
      const newComment = response.data.comment;

      // Update local state using functional update
      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          if (p._id === postId) {
            const currentComments = Array.isArray(p.comments) ? p.comments : [];
            return {
              ...p,
              comments: [...currentComments, newComment],
            };
          }
          return p;
        })
      );

      toast.success('Comment added');
      return true;
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      return false;
    }
  };

  const handleDelete = async (postId) => {
    console.log('Feed: Attempting to delete post:', postId);
    try {
      const response = await postAPI.deletePost(postId);
      console.log('Feed: Delete response:', response.data);
      
      // Remove post from local state
      setPosts((prevPosts) => prevPosts.filter((p) => p._id !== postId));
      toast.success('Post deleted successfully');
    } catch (error) {
      console.error('Feed: Error deleting post:', error);
      console.error('Feed: Error details:', {
        status: error.response?.status,
        data: error.response?.data
      });
      
      const errorMsg = error.response?.data?.detail || 'Failed to delete post';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 pb-6" data-testid="feed-skeleton">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-2xl bg-card shadow-sm p-0 overflow-hidden mb-4">
            {/* Header skeleton */}
            <div className="flex items-center space-x-3 px-5 py-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3 w-40" />
              </div>
              <Skeleton className="w-8 h-8 rounded-full" />
            </div>
            {/* Content skeleton */}
            <div className="px-5 pb-3 space-y-2">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-3/4" />
            </div>
            {/* Image skeleton */}
            <Skeleton className="w-full h-72" />
            {/* Actions skeleton */}
            <div className="flex items-center space-x-6 px-5 py-3">
              <Skeleton className="h-5 w-14" />
              <Skeleton className="h-5 w-10" />
              <Skeleton className="h-5 w-14" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12 skeleton-fade-in">
        <p className="text-gray-500 dark:text-gray-400">No posts yet. Start following people or create your first post!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-6 skeleton-fade-in">
      {posts.map((post) => (
        <PostCard
          key={post._id}
          post={post}
          onLike={handleLike}
          onComment={handleComment}
          onDelete={handleDelete}
        />
      ))}

      {/* Infinite scroll trigger */}
      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-4">
          {loadingMore && (
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-sm">Loading more posts...</span>
            </div>
          )}
        </div>
      )}

      {/* End of feed message */}
      {!hasMore && posts.length > 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">You're all caught up! 🎉</p>
        </div>
      )}
    </div>
  );
};

export default Feed;
